import type {Dispatch} from 'react'
import {Button, CheckboxGroup, Dialog, DialogTrigger, Label, Popover} from 'react-aria-components'

import ChevronDownSvg from '@/assets/icons/chevron-down.svg?react'
import Checkbox from '@/components/Checkbox/Checkbox'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './FilterAirlines.module.scss'

interface Props {
  includedAirlines: string[]
  onIncludedAirlinesChange: Dispatch<string[]>
  airlines: string[]
}

export default memo<Props>(function FilterAirlines(props) {
  const isIncludeAll =
    props.includedAirlines.length === props.airlines.length || props.includedAirlines.length === 0
  const isInclude = (() => {
    return props.includedAirlines.length <= props.airlines.length / 2
  })()

  const excludedAirlines = useMemo(() => {
    return Array.from(new Set(props.airlines).difference(new Set(props.includedAirlines)))
  }, [props.airlines, props.includedAirlines])

  const valueTextHelper = isInclude ? 'Only ' : 'Exclude: '
  const valueText = (() => {
    let text = ''
    if (isInclude && props.includedAirlines[0]) {
      text += props.includedAirlines[0]
      if (props.includedAirlines.length > 1) {
        text += ` +${props.includedAirlines.length - 1}`
      }
    } else {
      text += excludedAirlines[0]
      if (excludedAirlines.length > 1) {
        text += ` +${excludedAirlines.length - 1}`
      }
    }
    return text
  })()

  return (
    <DialogTrigger>
      <Button className={style.Button}>
        <div className={style.Value}>
          <span>
            <VisuallyHidden isVisible={isIncludeAll}>Airlines &nbsp;</VisuallyHidden>
            <VisuallyHidden>filter</VisuallyHidden>
            {!isIncludeAll && (
              <>
                {valueTextHelper}
                {valueText}
              </>
            )}
          </span>
        </div>
        <span aria-hidden className={style.Chevron}>
          <ChevronDownSvg
            className={style.ChevronIcon}
            width={12}
            height={16}
            viewBox='0 0 24 24'
            aria-hidden
          />
        </span>
      </Button>
      <Popover className={style.Popover}>
        <Dialog className={style.Dialog}>
          <div className={style.Heading} aria-hidden>
            Airlines
          </div>
          <div className={style.ClearButtonContainer}>
            <PrimaryButton
              size='sm'
              className={style.ClearButton}
              onPress={() => {
                props.onIncludedAirlinesChange(props.airlines)
              }}
            >
              Select all <VisuallyHidden>airlines</VisuallyHidden>
            </PrimaryButton>
            <PrimaryButton
              size='sm'
              className={style.ClearButton}
              onPress={() => {
                props.onIncludedAirlinesChange([])
              }}
            >
              Deselect all <VisuallyHidden>airlines</VisuallyHidden>
            </PrimaryButton>
          </div>
          <CheckboxGroup
            className={style.CheckboxGroup}
            value={props.includedAirlines}
            onChange={props.onIncludedAirlinesChange}
          >
            <VisuallyHidden as={Label}>Airlines</VisuallyHidden>
            {props.airlines.map(airline => (
              <Checkbox
                key={airline}
                label={airline}
                isLabelVisible
                value={airline}
                className={style.Checkbox}
                labelClassName={style.Label}
              />
            ))}
          </CheckboxGroup>
        </Dialog>
      </Popover>
    </DialogTrigger>
  )
})
